import moment from 'moment';

import { Size } from '../../typings/common';
import {
    ApiGalleryItem,
    AudioApiGalleryItem,
    ImageApiGalleryItem,
    PackshotApiGalleryItem,
    VideoApiGalleryItem
} from '../common/requestsTypings';
import { ResourceType } from '../common/typings';
import {
    GalleryAudioCollectionItem,
    GalleryCollectionItem,
    GalleryCommonCollectionItem,
    GalleryImageCollectionItem,
    GalleryPaneTypes,
    GalleryVideoCollectionItem
} from '../GalleryPane/typings';

function getApiItemSize(data: ImageApiGalleryItem): Partial<Size> {
    const metadataSource = data.stillageFileInfo || data;
    const metadataInfo = metadataSource.metadataInfo || {};

    return {
        width: metadataInfo.width,
        height: metadataInfo.height
    };
}

function parseApiCommonData(type: GalleryPaneTypes, data: ApiGalleryItem): GalleryCommonCollectionItem {
    const { id, date, archive, url, originalName } = data;

    return {
        removable: type === GalleryPaneTypes.recent,
        id,
        date,
        archive,
        url,
        originalName
    };
}

function parseApiImageData(
    type: GalleryPaneTypes,
    data: ImageApiGalleryItem | PackshotApiGalleryItem
): GalleryImageCollectionItem {
    const { previewUrl, largePreviewUrl, thumbnailUrl } = data;

    // name нужен только на вкладке GalleryPaneTypes.recent
    const name = type === GalleryPaneTypes.recent ? data.name : undefined;

    const common = {
        ...parseApiCommonData(type, data),
        name,
        previewUrl,
        largePreviewUrl,
        thumbnailUrl
    };

    if (data.type === undefined) {
        const { cropSourceUrl, stillageFileId, stillageFileInfo, stockFileId, categories, originalFileId } = data;
        const size = getApiItemSize(data);
        const width = size.width;
        const height = size.height;

        return {
            ...common,
            cropSourceUrl,
            stillageFileId,
            stillageFileInfo,
            stockFileId,
            categories,
            originalFileId,
            width,
            height,
            type: data.type
        };
    }

    const { sub_categories, formats, stillage_id, status } = data;

    return {
        ...common,
        sub_categories,
        formats,
        stillage_id,
        status,
        type: data.type
    };
}

function parseApiVideoData(type: GalleryPaneTypes, data: VideoApiGalleryItem): GalleryVideoCollectionItem {
    const {
        thumbnailUrl,
        sub_categories,
        formats,
        stillage_id,
        conversion_task_id,
        stock_file_id,
        overlayColor,
        thumbnail,
        status
    } = data;

    // name нужен только на вкладке GalleryPaneTypes.recent
    const name = type === GalleryPaneTypes.recent ? data.name : undefined;

    return {
        ...parseApiCommonData(type, data),
        name,
        thumbnailUrl,
        thumbnail,
        overlayColor,
        sub_categories,
        formats,
        stillage_id,
        conversion_task_id,
        stock_file_id,
        status,
        type: data.type
    };
}

function parseApiAudioData(type: GalleryPaneTypes, data: AudioApiGalleryItem): GalleryAudioCollectionItem {
    const { name, url, duration, sub_categories, stock_file_id } = data;

    return {
        ...parseApiCommonData(type, data),
        name,
        url,
        durationLabel: moment((duration || 0) * 1000).format('mm:ss'),
        duration,
        type: data.type,
        sub_categories,
        stock_file_id
    };
}

const ApiDataParsers: Record<
    ResourceType.audio | ResourceType.image | ResourceType.video,
    (type: GalleryPaneTypes, data: ApiGalleryItem) => GalleryCollectionItem
> = {
    [ResourceType.audio]: parseApiAudioData,
    [ResourceType.image]: parseApiImageData,
    [ResourceType.video]: parseApiVideoData
};

export class GalleryItemHelper {
    public static parseApiDataList(
        resourceType: ResourceType,
        type: GalleryPaneTypes,
        dataList: ApiGalleryItem[]
    ): GalleryCollectionItem[] {
        if (resourceType === ResourceType.overlay) {
            throw `Resource type ${resourceType} is not supported`;
        }

        const parser = ApiDataParsers[resourceType];
        return dataList.map(data => parser(type, data));
    }

    public static calcSmallPreviewSizes(data: GalleryImageCollectionItem): Size {
        const { width, height } = data;

        // это размеры превью из gallery-item__item-content
        const MIN_SIZES = {
            HEIGHT: 159,
            WIDTH: 282
        };

        const SIZES = {
            HEIGHT: 337,
            WIDTH: 500
        };

        const PADDING = 10;
        const SPACE = PADDING * 10;

        // например для снэпшотов width и height сейчас не приходят
        if (!width || !height) {
            return {
                height: SIZES.HEIGHT + PADDING * 2,
                width: SIZES.WIDTH + PADDING * 2
            };
        }

        const aspect = height / width;

        if (aspect > 1) {
            const targetHeight = Math.min(SIZES.HEIGHT, Math.max(height + SPACE, MIN_SIZES.HEIGHT));

            return {
                height: targetHeight + PADDING * 2,
                width: targetHeight / aspect + PADDING * 2
            };
        } else {
            const targetWidth = Math.min(SIZES.WIDTH, Math.max(width + SPACE, MIN_SIZES.WIDTH));

            return {
                height: targetWidth * aspect + PADDING * 2,
                width: targetWidth + PADDING * 2
            };
        }
    }
}
